'use client'

import { Check, ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { cn } from '../cn'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

// -- Single-select ----------------------------------------------------------------

interface FilterSelectProps {
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
}

function FilterSelect({ value, onChange, options }: FilterSelectProps) {
  const [open, setOpen] = useState(false)
  const selected = options.find((o) => o.value === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 rounded-lg border border-input bg-background px-2.5 py-1 text-xs text-foreground hover:bg-accent focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors">
          <span>{selected?.label ?? value}</span>
          <ChevronDown className={cn('w-3.5 h-3.5 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} className="p-1 min-w-[160px] w-auto">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              onChange(option.value)
              setOpen(false)
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded-sm px-3 py-1.5 text-sm text-left hover:bg-accent transition-colors',
              value === option.value && 'font-medium text-primary',
            )}>
            <Check className={cn('w-4 h-4 shrink-0', value === option.value ? 'opacity-100' : 'opacity-0')} />
            {option.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}

// -- Multi-select -----------------------------------------------------------------

interface FilterMultiSelectProps {
  values: Set<string>
  onChange: (values: Set<string>) => void
  options: { value: string; label: string }[]
  placeholder: string
  filterName?: string
}

function FilterMultiSelect({ values, onChange, options, placeholder, filterName }: FilterMultiSelectProps) {
  const [open, setOpen] = useState(false)

  function toggle(v: string) {
    const next = new Set(values)
    if (next.has(v)) {
      next.delete(v)
    } else {
      next.add(v)
    }
    onChange(next)
  }

  const label =
    values.size === 0
      ? placeholder
      : values.size === 1
        ? (options.find((o) => o.value === Array.from(values)[0])?.label ?? placeholder)
        : `${values.size} ${filterName || 'selected'}`

  const hasSelection = values.size > 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg border bg-background px-2.5 py-1 text-xs hover:bg-accent focus:outline-none focus:ring-1 focus:ring-primary transition-colors',
            hasSelection ? 'border-primary text-primary font-medium' : 'border-input text-foreground',
          )}>
          <span>{label}</span>
          <ChevronDown className={cn('w-3.5 h-3.5 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} className="p-1 min-w-[180px] w-auto">
        {hasSelection && (
          <>
            <button
              type="button"
              onClick={() => onChange(new Set())}
              className="flex w-full items-center gap-2 rounded-sm px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent transition-colors">
              Clear selection
            </button>
            <div className="my-1 border-t border-border" />
          </>
        )}
        {options.map((option) => {
          const checked = values.has(option.value)
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => toggle(option.value)}
              className="flex w-full items-center gap-2.5 rounded-sm px-3 py-1.5 text-sm text-left hover:bg-accent transition-colors">
              <span
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                  checked ? 'bg-primary border-primary' : 'border-input',
                )}>
                {checked && <Check className="w-3 h-3 text-primary-foreground" strokeWidth={3} />}
              </span>
              {option.label}
            </button>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}

export { FilterMultiSelect, type FilterMultiSelectProps, FilterSelect, type FilterSelectProps }
